import {
  FlexGrid as Grid,
  ActionableNotification,
  Button,
  InlineNotification,
  Loading,
  Modal,
  Tag,
} from '@carbon/react';
import {
  AddComment,
  Checkmark,
  Certificate,
  Clean,
  CopyFile,
  Launch,
  Undo,
  Workspace as WorkspaceIcon,
} from '@carbon/react/icons';
import { useCallback, useEffect, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { useLocation, useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { parseMomentUtc } from 'utils/dates';

import { api } from 'lib/request/api';
import { PageHeader } from 'components/page-header';
import { EntityFlagTag } from 'components/entity-flag-tag';
import { OperatorTag } from 'components/operator-tag';
import { PlainLink } from 'components/link';
import { format } from 'lib/ui';
import { EntityResponse } from 'types/api-responses';
import { pageTitle } from 'utils/page-title';
import { entityIdSuffix } from 'utils/entity-id';
import { EntitiesAnchorJumpMenu, EntityForm } from 'components/entity-form';
import {
  ActivityFeedPanel,
  isActivityFeedPanelOpenAtom,
} from 'components/activity-feed-panel';

import { EntityTabs } from './entity-tabs';
import { CreateWorkspaceModal } from './interest-certification/create-workspace-modal';
import { WorkspacesPanel } from './interest-certification/workspaces-panel';
import { WorkspaceCreationProgress } from './interest-certification/workspace-creation-progress';
import { workspacesCountAtomFamily } from './interest-certification/atoms';
import type { CreateWorkspaceResponse } from './interest-certification/types';
// Phase 2 (default-to-dedupe): the Entity Profile page launches the same
// two-panel Entity Consolidation tearsheet as the entity search list (§3.1) —
// identical props and onComplete contract, so it's a drop-in for the legacy sheet.
import ReconcileTearsheet from './reconcile/entity-consolidation-tearsheet';
import {
  useEntityReconcile,
  type ReconcileSummary,
} from './reconcile/use-entity-reconcile';
import { NoReconcileCandidatesModal } from './reconcile/no-reconcile-candidates-modal';
import { UndoReconcileModal } from './reconcile/undo-reconcile-modal';
import style from './page.module.scss';

// Post-reconcile banner copy, matching the mock EntityDetailPage. When the
// viewed entity was retired the examiner is now on the surviving entity's
// profile, so the banner explains the transition and warns that Undo navigates
// back (narrative Case 2 / UI Principle #7).
const reconcileBannerText = (summary: ReconcileSummary) => {
  const { result } = summary;
  if (result.viewedEntityRetiredInto) {
    return {
      title: `You are now viewing ${
        result.viewedEntityRetiredInto.name
      } ${entityIdSuffix(result.viewedEntityRetiredInto.id)}.`,
      subtitle: `${summary.fromEntityName} was reconciled into this entity. Undo Reconcile will reverse everything and return you to the original entity's profile.`,
    };
  }
  return {
    title: 'Reconcile complete —',
    subtitle: summary.bullets.join(' '),
  };
};

const DeduplicatedBanner = ({ entity }: { entity: EntityResponse }) => {
  const mergedInto = entity.mergedInto;
  return (
    <div className={style.readOnlyBanner}>
      <InlineNotification
        kind="info"
        lowContrast
        hideCloseButton
        title="This entity was deduplicated."
        subtitle="This is a read-only view of an entity that was merged into another."
      />
      {mergedInto && (
        <p className={style.bannerDetailLine}>
          Merged into{' '}
          <PlainLink href={mergedInto.href}>{mergedInto.name}</PlainLink>{' '}
          {entityIdSuffix(mergedInto.id)}.
        </p>
      )}
    </div>
  );
};

const DeletedBanner = ({ entity }: { entity: EntityResponse }) => {
  const who = entity.deletedBy;
  const when = entity.deletedAt
    ? parseMomentUtc(entity.deletedAt).format('MM/DD/YYYY')
    : null;
  return (
    <div className={style.readOnlyBanner}>
      <InlineNotification
        kind="info"
        lowContrast
        hideCloseButton
        title="This entity has been deleted."
        subtitle="This is a read-only view of a deleted entity."
      />
      {(who || when) && (
        <p className={style.bannerDetailLine}>
          Deleted{who ? ` by ${who}` : ''}
          {when ? ` on ${when}` : ''}.
        </p>
      )}
    </div>
  );
};

const PageContent = ({
  entity,
  getEntity,
  loading,
  readOnly,
}: {
  entity: EntityResponse | undefined;
  getEntity: () => Promise<void>;
  loading: boolean;
  readOnly: boolean;
}) => {
  const entityType = entity ? entity.type : undefined;

  return (
    <Grid className={style.entityPageContainer}>
      {entity &&
        readOnly &&
        (entity.deduplicatedAt ? (
          <DeduplicatedBanner entity={entity} />
        ) : (
          <DeletedBanner entity={entity} />
        ))}
      {/* The anchor menu jumps to sections (incl. Entity Relationships) that the
          read-only deduplicated view omits, so its links would be broken there. */}
      {!readOnly && <EntitiesAnchorJumpMenu />}
      {/* A disabled fieldset makes every native form control inside the
          EntityForm read-only without threading a prop through each field. */}
      <fieldset disabled={readOnly} className={style.entityFormFieldset}>
        <EntityForm
          key={entityType}
          entity={entity}
          getEntity={getEntity}
          loading={loading}
          entityTypeValue={entity ? entity.type : undefined}
          hideSubmitButton={readOnly}
        />
      </fieldset>
      {/* The tabs (aliases, assets, capacities, …) don't apply to a deduplicated
          entity and their requests can 500, so omit them in read-only mode. */}
      {entity && !readOnly && <EntityTabs entity={entity} />}
    </Grid>
  );
};

const HeaderActions = ({
  entity,
  getEntity,
  onCertifyInterestsClick,
  onViewWorkspacesClick,
  onReconcileClick,
  reconcileSignalCount,
  showUndoReconcile,
  onUndoReconcileClick,
}: {
  entity: EntityResponse;
  getEntity: () => Promise<void>;
  onCertifyInterestsClick: () => void;
  onViewWorkspacesClick: () => void;
  onReconcileClick: () => void;
  reconcileSignalCount: number;
  showUndoReconcile: boolean;
  onUndoReconcileClick: () => void;
}) => {
  const history = useHistory();
  const setActivityFeedOpen = useSetAtom(isActivityFeedPanelOpenAtom);
  const [countLoadable, refreshCount] = useAtom(
    workspacesCountAtomFamily(entity.id)
  );
  const [portfolioModalOpen, setPortfolioModalOpen] = useState(false);
  const [portfolioSaving, setPortfolioSaving] = useState(false);

  useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  const count =
    countLoadable.state === 'hasData' && countLoadable.data
      ? countLoadable.data.totalCount
      : 0;
  const showViewWorkspaces = count > 0;

  const isPortfolio = entity.portfolioInvestment;
  const [portfolioError, setPortfolioError] = useState<string | null>(null);

  const handlePortfolioConfirm = useCallback(async () => {
    setPortfolioSaving(true);
    setPortfolioError(null);
    try {
      await api.patch(`entities/${entity.id}`, {
        json: { portfolio_investment: !isPortfolio },
      });
      await getEntity();
      setPortfolioModalOpen(false);
    } catch {
      setPortfolioError(
        'Failed to update portfolio investment status. Please try again.'
      );
    } finally {
      setPortfolioSaving(false);
    }
  }, [entity.id, isPortfolio, getEntity]);

  return (
    <>
      <Button
        hasIconOnly
        kind="tertiary"
        size="md"
        renderIcon={AddComment}
        iconDescription="Activity"
        tooltipAlignment="center"
        tooltipPosition="bottom"
        onClick={() => setActivityFeedOpen(true)}
      />
      <Button
        kind="tertiary"
        size="md"
        renderIcon={isPortfolio ? Checkmark : Launch}
        onClick={() => setPortfolioModalOpen(true)}
      >
        {isPortfolio
          ? 'Portfolio Investment Entity'
          : 'Mark as Portfolio Investment Entity'}
      </Button>
      {portfolioModalOpen && (
        <Modal
          open
          size="sm"
          modalHeading={
            isPortfolio
              ? 'Remove entity from Portfolio Asset map layer?'
              : 'Include entity in Portfolio Asset map layer?'
          }
          primaryButtonText={
            portfolioSaving
              ? isPortfolio
                ? 'Removing…'
                : 'Including…'
              : isPortfolio
                ? 'Remove assets'
                : 'Include assets'
          }
          primaryButtonDisabled={portfolioSaving}
          danger={isPortfolio}
          secondaryButtonText="Cancel"
          onRequestSubmit={handlePortfolioConfirm}
          onRequestClose={() => {
            setPortfolioModalOpen(false);
            setPortfolioError(null);
          }}
        >
          {portfolioError && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title="Error"
              subtitle={portfolioError}
            />
          )}
          <p>
            {isPortfolio ? (
              <>
                Unmarking <strong>{entity.name}</strong> as a Portfolio
                Investment Entity will remove its assets from the Portfolio
                Asset map layer. They will no longer appear on that layer.
              </>
            ) : (
              <>
                Marking <strong>{entity.name}</strong> as a Portfolio Investment
                Entity will include its assets in the Portfolio Asset map layer.
                They will become visible to everyone with access to that layer.
              </>
            )}
          </p>
        </Modal>
      )}
      {showViewWorkspaces && (
        <Button
          kind="tertiary"
          size="md"
          renderIcon={WorkspaceIcon}
          onClick={onViewWorkspacesClick}
        >
          View Workspaces ({format.number(count)})
        </Button>
      )}
      <Button
        kind="tertiary"
        renderIcon={(props) => <CopyFile size={20} {...props} />}
        size="md"
        onClick={() => history.push(`${entity.resources.deduplication.href}`)}
      >
        <span>Deduplicate</span>
      </Button>
      <Button
        kind="tertiary"
        size="md"
        renderIcon={Certificate}
        onClick={onCertifyInterestsClick}
      >
        Certify Interests
      </Button>
      {showUndoReconcile && (
        <Button
          kind="danger--tertiary"
          size="md"
          renderIcon={(props) => <Undo size={16} {...props} />}
          onClick={onUndoReconcileClick}
        >
          Undo Reconcile
        </Button>
      )}
      <Button
        renderIcon={(props) => <Clean size={20} {...props} />}
        size="md"
        disabled={reconcileSignalCount <= 0}
        onClick={onReconcileClick}
      >
        <span>Reconcile</span>
      </Button>
    </>
  );
};

// Read-only (deduplicated) entities keep the "Activity" affordance so the
// merge/reconcile history is reachable, but drop the write actions (Deduplicate,
// Certify, View Workspaces) that don't apply to a retired entity.
const ReadOnlyHeaderActions = () => {
  const setActivityFeedOpen = useSetAtom(isActivityFeedPanelOpenAtom);

  return (
    <Button
      hasIconOnly
      kind="tertiary"
      size="md"
      renderIcon={AddComment}
      iconDescription="Activity"
      tooltipAlignment="center"
      tooltipPosition="bottom"
      onClick={() => setActivityFeedOpen(true)}
    />
  );
};

const Header = ({
  entity,
  readOnly,
  getEntity,
  onCertifyInterestsClick,
  onViewWorkspacesClick,
  onReconcileClick,
  reconcileSignalCount,
  showUndoReconcile,
  onUndoReconcileClick,
}: {
  entity: EntityResponse | undefined;
  readOnly: boolean;
  getEntity: () => Promise<void>;
  onCertifyInterestsClick: () => void;
  onViewWorkspacesClick: () => void;
  onReconcileClick: () => void;
  reconcileSignalCount: number;
  showUndoReconcile: boolean;
  onUndoReconcileClick: () => void;
}) => {
  const { pathname } = useLocation();
  const title = entity ? `${entity.name} ${entityIdSuffix(entity.id)}` : '';

  return (
    <PageHeader
      breadcrumbs={[
        ['/entities', 'Entities'],
        [pathname, readOnly ? 'Deduplicated Entity' : 'Entity Profile'],
      ]}
      title={
        entity ? (
          <div className={style.titleWithFlag}>
            <h1>{title}</h1>
            {entity.portfolioInvestment && (
              <Tag type="teal" size="sm">
                Portfolio
              </Tag>
            )}
            {entity.operator && <OperatorTag />}
            {/* Flag rationale is only actionable on a live entity — a
                deduplicated one has already been retired. */}
            {!readOnly && (
              <EntityFlagTag
                flagCategory={entity.flagCategory}
                flagRationale={entity.flagRationale}
                flagSeverity={entity.flagSeverity}
              />
            )}
          </div>
        ) : (
          ''
        )
      }
      stickyHeader
      actions={
        entity ? (
          readOnly ? (
            <ReadOnlyHeaderActions />
          ) : (
            <HeaderActions
              entity={entity}
              getEntity={getEntity}
              onCertifyInterestsClick={onCertifyInterestsClick}
              onViewWorkspacesClick={onViewWorkspacesClick}
              onReconcileClick={onReconcileClick}
              reconcileSignalCount={reconcileSignalCount}
              showUndoReconcile={showUndoReconcile}
              onUndoReconcileClick={onUndoReconcileClick}
            />
          )
        ) : null
      }
    />
  );
};

const ShowEntityPage = ({
  entity,
  loading,
  getEntity,
}: {
  entity: EntityResponse | undefined;
  loading: boolean;
  getEntity: () => Promise<void>;
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  // Kickoff response for an in-flight/just-finished workspace creation; drives
  // the live progress chip. Null when there's nothing building.
  const [workspaceCreation, setWorkspaceCreation] =
    useState<CreateWorkspaceResponse | null>(null);
  const location = useLocation();
  const reconcile = useEntityReconcile(entity, getEntity);

  // Auto-open the Create Workspace modal when navigated with ?openIC=true
  // (used by the "System Promoted" pill — user wants to certify this slot).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openIC') === 'true') {
      setModalOpen(true);
    }
  }, [location.search]);

  // A deduplicated (soft-deleted) entity renders read-only: its demographics are
  // shown but cannot be edited, and the action buttons (Deduplicate, Certify, etc.)
  // are suppressed. A banner links to the surviving entity it was merged into.
  const readOnly = !!entity?.deduplicatedAt || !!entity?.deleted;

  return (
    <>
      <Header
        entity={entity}
        readOnly={readOnly}
        getEntity={getEntity}
        onCertifyInterestsClick={() => setModalOpen(true)}
        onViewWorkspacesClick={() => setPanelOpen(true)}
        onReconcileClick={reconcile.launch}
        reconcileSignalCount={reconcile.signalCount}
        showUndoReconcile={!!reconcile.summary}
        onUndoReconcileClick={reconcile.requestUndo}
      />
      {entity && (
        <ActivityFeedPanel
          modelType="Entity"
          modelId={entity.id}
          title="Activity"
          subtitle={entity.name}
          // A deduplicated (read-only) entity can't take new comments — its
          // history was moved to the surviving entity at merge time — so pass
          // an empty href to suppress the comment input.
          commentsHref={readOnly ? '' : `/entities/${entity.id}/comments`}
        />
      )}
      {entity && !readOnly && (
        <>
          <Helmet>
            <title>{pageTitle(`${entity.name} | Entity Profile`)}</title>
          </Helmet>
          <CreateWorkspaceModal
            entityId={entity.id}
            entityName={entity.name}
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            onCreated={setWorkspaceCreation}
          />
          {workspaceCreation && (
            <WorkspaceCreationProgress
              creation={workspaceCreation}
              onDismiss={() => setWorkspaceCreation(null)}
            />
          )}
          <WorkspacesPanel
            entityId={entity.id}
            open={panelOpen}
            onClose={() => setPanelOpen(false)}
          />
        </>
      )}
      {entity && readOnly && (
        <Helmet>
          <title>{pageTitle(`${entity.name} | Deduplicated Entity`)}</title>
        </Helmet>
      )}

      {reconcile.assembling && (
        <Loading
          description="Assembling reconciliation candidates…"
          withOverlay
        />
      )}

      {/* Post-reconcile summary banner (with Undo) takes precedence over the
          similar-entities signal — matching the mock EntityDetailPage. */}
      {entity && !readOnly && (
        <div className={style.reconcileBanners}>
          {reconcile.launchError && (
            <InlineNotification
              kind="error"
              lowContrast
              title="Reconcile unavailable —"
              subtitle={reconcile.launchError}
              onCloseButtonClick={reconcile.dismissLaunchError}
            />
          )}
          {reconcile.summary ? (
            <>
              <InlineNotification
                kind="success"
                lowContrast
                title={reconcileBannerText(reconcile.summary).title}
                subtitle={reconcileBannerText(reconcile.summary).subtitle}
                onCloseButtonClick={reconcile.dismissSummary}
              />
              {/* Pre-existing legacy hybrid alias groups were included (§3.5 / #7):
                  disclosed, not blocked. */}
              {(reconcile.summary.result.legacyHybridWarnings ?? []).length >
                0 && (
                <InlineNotification
                  kind="warning"
                  lowContrast
                  hideCloseButton
                  title="Existing mixed-type alias group —"
                  subtitle={`${reconcile.summary.result.legacyHybridWarnings
                    .map((w) => w.name)
                    .join(
                      ', '
                    )} already links an Individual and an Organization from before type checks were enforced. The reconcile was completed; no new mixed-type link was created.`}
                />
              )}
            </>
          ) : reconcile.signalVisible ? (
            <ActionableNotification
              inline
              kind="info"
              lowContrast
              // The banner mounts async (when the signal call returns) — the
              // default hasFocus/alertdialog behavior steals focus, which
              // swallows the next click elsewhere and scrolls the page back to
              // the banner. A passive suggestion must not take focus.
              hasFocus={false}
              role="status"
              title={`We found ${reconcile.signalCount} entities that may be similar to this one. Would you like to reconcile them?`}
              actionButtonLabel="Reconcile"
              onActionButtonClick={reconcile.launch}
              onCloseButtonClick={reconcile.dismissSignal}
            />
          ) : null}
        </div>
      )}

      {/* Mounted only while open: a closed-but-mounted Carbon Modal still
          renders its children into the DOM (hidden), leaking the candidate
          rows into the page; mount-on-open also lets the tearsheet initialize
          its selection per launch. */}
      {entity && !readOnly && reconcile.tearsheetOpen && (
        <ReconcileTearsheet
          open
          onClose={reconcile.closeTearsheet}
          viewedEntity={entity}
          candidates={reconcile.candidates}
          viewedAliasGroup={reconcile.viewedAliasGroup}
          moreFound={reconcile.moreFound}
          totalBeforeCap={reconcile.totalBeforeCap}
          sessionId={reconcile.sessionId}
          onComplete={reconcile.complete}
          onCanonized={reconcile.notchCanonize}
        />
      )}

      <NoReconcileCandidatesModal
        open={reconcile.allEliminatedOpen}
        onClose={reconcile.closeAllEliminated}
      />

      {/* Undo Reconcile confirmation (latest mock) — bullets out what reverses. */}
      <UndoReconcileModal
        open={reconcile.undoModalOpen}
        undoing={reconcile.undoing}
        bullets={reconcile.summary?.bullets ?? []}
        addAsNewCount={reconcile.summary?.addAsNewCount ?? 0}
        undoError={reconcile.undoError}
        onCancel={reconcile.cancelUndo}
        onConfirm={reconcile.confirmUndo}
      >
        {reconcile.summary?.result.viewedEntityRetiredInto && (
          <p>You will be returned to the original entity&apos;s profile.</p>
        )}
      </UndoReconcileModal>

      <PageContent
        entity={entity}
        getEntity={getEntity}
        loading={loading}
        readOnly={readOnly}
      />
    </>
  );
};

export { ShowEntityPage };
